<script lang="ts" setup>
import { modalController } from '@ionic/vue';
import BaseButton from '@/atoms/BaseButton.vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import { useCamera } from '@/composables/useCamera';
import { useSnackBar } from '@/composables/useSnackBar';

const props = defineProps<{ withUsePhoto?: boolean }>();

const { photo, isLoading, error, takePhoto, pickMultipleFromGallery, saveToGallery, clearPhoto } =
  useCamera();
const { show: showSnackBar } = useSnackBar();

const dismiss = () => modalController.dismiss();

const handleUsePhoto = () => {
  if (!photo.value) return;
  modalController.dismiss({ photo: photo.value });
};

const handleTakePhoto = async () => {
  await takePhoto();
};

const handlePickMultipleFromGallery = async () => {
  const photos = await pickMultipleFromGallery();
  if (photos.length) modalController.dismiss({ photos });
};

const handleSaveToGallery = async () => {
  if (!photo.value) return;
  const saved = await saveToGallery(photo.value);
  if (saved) {
    showSnackBar({ text: 'Photo enregistrée dans la galerie !' });
    modalController.dismiss({ saved: true });
  }
};

const handleRetake = () => {
  clearPhoto();
};
</script>

<template>
  <div class="flex flex-col pt-6 px-4 rounded-xl bg-bg-primary">
    <!-- Handle bar -->
    <div class="w-14 h-1 bg-bg-quaternary rounded-full self-center mb-4" />

    <BaseLabel text="Appareil photo" preset="h3" weight="medium" class="mb-1" />
    <BaseLabel
      text="Prenez une photo ou choisissez depuis votre galerie"
      preset="medium"
      color="secondary"
      weight="regular"
      class="mb-4"
    />

    <!-- Photo preview -->
    <div v-if="photo" class="flex flex-col gap-4">
      <div class="relative rounded-xl overflow-hidden aspect-[4/3] bg-grey-100">
        <img :src="photo" alt="Photo prise" class="w-full h-full object-cover" />
      </div>

      <div class="flex flex-col gap-3 pb-safe-bottom pb-4">
        <BaseButton
          v-if="withUsePhoto"
          label="Utiliser cette photo"
          type="primary"
          icon-name="check"
          @click="handleUsePhoto"
        />
        <BaseButton
          label="Enregistrer dans la galerie"
          :type="withUsePhoto ? 'secondary' : 'primary'"
          icon-name="download"
          :state="isLoading ? 'loading' : 'default'"
          @click="handleSaveToGallery"
        />
        <BaseButton
          label="Reprendre une photo"
          type="secondary"
          icon-name="camera"
          :state="isLoading ? 'loading' : 'default'"
          @click="handleRetake"
        />
        <BaseButton label="Fermer" type="ghost" @click="dismiss" />
      </div>
    </div>

    <!-- Actions initiales -->
    <div v-else class="flex flex-col gap-3 pb-safe-bottom pb-4">
      <div v-if="error" class="bg-error-primary rounded-lg px-3 py-2">
        <BaseLabel :text="error" preset="medium" color="error" />
      </div>

      <BaseButton
        label="Prendre une photo"
        type="primary"
        icon-name="camera"
        :state="isLoading ? 'loading' : 'default'"
        @click="handleTakePhoto"
      />
      <BaseButton
        label="Choisir depuis la galerie"
        type="secondary"
        icon-name="image"
        :state="isLoading ? 'loading' : 'default'"
        @click="handlePickMultipleFromGallery"
      />
      <BaseButton label="Annuler" type="ghost" @click="dismiss" />
    </div>
  </div>
</template>
